/* ============================================================
   MagnuMaster — consent, analytics & advertising pixels
   ------------------------------------------------------------
   Loaded on every public page, before any other script. IDs live
   here only, so a change is a one-file edit rather than 24 edits.

   - Meta Pixel        794784032223260
   - Google tag        GT-NF7QHSSD — the account's one container tag; it feeds
                       GA4 G-03E28HFWXX, GA4 G-93VXQFP0XG and Ads
                       AW-11102790094 from a single config (see below)
   - Microsoft Clarity xvllt9gzlx — session replay and heatmaps
   Domain verification (Meta) is a static <meta> tag in each page
   <head>, not here — Meta's crawler reads raw HTML, not injected
   tags.

   CONSENT (GDPR / EU ePrivacy)
   ---------------------------
   Two different mechanisms, because the two vendors are not the
   same kind of tracker:

   - Meta  — no script is injected at all until Accept is clicked.
             Meta has no cookieless mode; it is pure marketing, so
             it stays behind a hard opt-in gate.
   - Clarity — loads for everyone, like Google, so the traffic figures
             are the real ones rather than the accepted-only subset.
             Until Accept it runs with `clarity('consent', false)`,
             which is Clarity's own cookieless mode: no `_clck` or
             `_clsk` is written and nothing persists between visits.
             On Accept we send `clarity('consent')` and it upgrades to
             normal cookie-based measurement.

             This is a business decision taken deliberately: the
             client needs to see every visit. Note that it does mean
             session *recording* starts before a visitor has agreed to
             it, which is a stronger claim than Google's anonymous
             pings. If that is ever challenged, the fix is to move
             bootClarity() back behind the Accept branch — the queue
             machinery for that is still here and still works.
   - Google — loads for everyone under **Consent Mode v2** with all
             storage denied by default. In that state gtag sets no
             cookies and no identifiers; it sends anonymous, non-
             persistent pings so GA4 can still count traffic and
             model the rest. On Accept we send a `consent update`
             and it upgrades to normal cookie-based measurement.

   That split is deliberate. Before it, a decline meant GA4 saw
   literally nothing, which is why GA4 reported a fraction of what
   Meta's ad-click "landing page views" reported.

   The choice is stored in localStorage under `mm_consent` for a
   year. Clearing that key re-opens the banner (handy for testing).

   DEBUGGING
   ---------
   Append to any URL — both persist for the browser tab (session),
   so they survive navigation across pages. `=0` turns them off.

     ?gadebug=1   sends GA4 hits flagged for **DebugView**
                  (GA4 → Admin → DebugView shows them live)
     ?mmdebug=1   traces consent state and every funnel event to
                  the browser console, prefixed [MM]

   FUNNEL API
   ----------
   window.MM_TRACK lets the rest of the site report the funnel
   without knowing any vendor API:

     MM_TRACK.viewContent(item)    product page opened
     MM_TRACK.addToCart(item)      item added to the cart
     MM_TRACK.beginCheckout(items) buyer sent to Shopify checkout
     MM_TRACK.lead(detail)         quote form submitted

   WHO REPORTS WHAT — read this before adding a vendor call
   --------------------------------------------------------
   The Shopify **Google & YouTube** and **Meta** sales channels are
   installed on the store, and their web pixels already report the
   whole commerce funnel — page_view, view_item, add_to_cart,
   begin_checkout and purchase — to this same Google tag and this
   same Meta pixel, largely from Shopify's own servers.

   So this file deliberately no longer sends those. Doing both counted
   every storefront event twice: Meta only de-duplicates when the two
   senders share an `event_id`, and two hits on one Google tag are
   simply two hits. Traffic read about double, and the funnel looked
   twice as leaky as it really is.

   The split that remains:

     Shopify's channels  page_view, view_item, add_to_cart,
                         begin_checkout, purchase — Google and Meta
     This file           generate_lead / Lead — the quote form, which
                         Shopify cannot see — plus every Clarity event
                         and the consent banner itself

   Clarity still receives all four MM_TRACK calls, as custom events
   plus an `mm_value` tag, so a replay can be filtered down to the
   sessions that actually converted. Shopify feeds Clarity nothing, so
   there is no duplication to undo there.

   Meta events raised before a decision are held in a small queue and
   flushed on Accept, so a Lead raised while the banner is still up is
   not lost. On Decline that queue is dropped.

   **Do not re-add a Google or Meta send to fire().** If the sales
   channels are ever removed, restore them here in the same change —
   but never while both exist. NEXT-STEPS.md §5 carries the history.
   ============================================================ */
(function () {
  'use strict';

  var META_ID = '794784032223260';
  var CLARITY_ID = 'xvllt9gzlx';
  /* The account's single Google tag. A `GT-` id is a container, not a
     property: the account owns the destination list, and ONE config call fans
     out to every entry in it. Today that list is

       G-03E28HFWXX     GA4   "magnumaster – GA4"
       G-93VXQFP0XG     GA4   "MonsterInsights" — the old WordPress property
       AW-11102790094   Google Ads

     Managed at Google Ads → Tools → Data manager → this tag → Destinations.
     Adding or removing a destination is therefore an account setting now, not
     a code change.

     Do NOT also config those three ids individually. They are already
     destinations of this tag, so a second config counts every hit twice —
     that is what Google means by "don't add more than one Google tag to each
     page". The site previously loaded G-03E28HFWXX on its own, which is why
     Google reported the tag as "not detected" and why only one of the two GA4
     properties ever filled up. */
  var GOOGLE_TAG = 'GT-NF7QHSSD';
  var CUR = 'EUR';

  /* Cross-domain measurement. Checkout leaves this site for Shopify, so
     without this the buyer arrives as a brand-new user from a referral and
     the sale is attributed to "magnumaster.at" instead of the ad.
     NOTE: this half only decorates outgoing links. The same list must also
     be entered in GA4 → Admin → Data streams → Configure tag settings →
     Configure your domains, or GA4 ignores the incoming _gl parameter. */
  var LINKED_DOMAINS = [
    'magnumaster.at',
    'www.magnumaster.at',
    'magnumaster.myshopify.com',
    'shopify.com',
    'shop.app'
  ];

  var KEY = 'mm_consent';
  var TTL = 365 * 24 * 60 * 60 * 1000;   // re-ask after a year

  var metaBooted = false;
  var metaQueue = [];
  var clarityBooted = false;
  var clarityQueue = [];

  /* ---- debug switches -------------------------------------- */
  // ?name=1 turns on for the tab, ?name=0 off. Sticky so it survives the
  // page-to-page navigation you need to actually watch a funnel.
  function flag(name) {
    var m = new RegExp('[?&]' + name + '=([01])').exec(location.search);
    try {
      if (m) {
        if (m[1] === '1') { sessionStorage.setItem('mm_' + name, '1'); return true; }
        sessionStorage.removeItem('mm_' + name); return false;
      }
      return sessionStorage.getItem('mm_' + name) === '1';
    } catch (e) { return !!m && m[1] === '1'; }   // storage blocked
  }
  var GA_DEBUG = flag('gadebug');
  var MM_DEBUG = flag('mmdebug');

  function log() {
    if (!MM_DEBUG || !window.console || !console.log) return;
    try {
      console.log.apply(console, ['%c[MM]', 'color:#B8893A;font-weight:600']
        .concat([].slice.call(arguments)));
    } catch (e) { }
  }

  /* ---- stored decision ------------------------------------ */
  function saved() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      if (!v || !v.state) return null;
      if (Date.now() - (v.at || 0) > TTL) return null;   // expired → ask again
      return v.state;
    } catch (e) { return null; }       // private mode / storage disabled
  }
  function remember(state) {
    try { localStorage.setItem(KEY, JSON.stringify({ state: state, at: Date.now() })); }
    catch (e) { /* storage blocked — the choice lasts this page only */ }
  }

  /* ---- tell Shopify the same thing -------------------------
     On the Shopify theme there were two cookie notices on screen at once:
     this one, and Shopify's own from Settings → Customer privacy (client
     review 27.08.2026). Only one can be shown, and this is the one that
     is branded, translated into all five languages and actually wired to
     Google, Clarity and Meta — so Shopify's is switched off in the admin
     and this banner reports the decision into Shopify's Customer Privacy
     API instead. That API is what gates Shopify's own marketing pixels
     and any app that reads consent, so skipping this step would silence
     the banner *and* the thing it is there to control.

     Everything here is defensive: window.Shopify only exists on the
     theme, loadFeatures only on a storefront page, and neither exists on
     the static site — where this quietly does nothing, as it should. */
  function tellShopify(state, fromClick) {
    var granted = state === 'granted';
    var consent = {
      analytics: granted,
      marketing: granted,
      preferences: granted,
      sale_of_data: granted
    };
    function send() {
      try {
        var cp = window.Shopify && window.Shopify.customerPrivacy;
        if (!cp || typeof cp.setTrackingConsent !== 'function') return;
        /* Shopify is explicit that consent must be recorded from a visitor
           interaction, never on their behalf. On a page load we are replaying
           a decision this visitor already made, so only write it when Shopify
           has no record of its own or is out of step — never as a routine
           overwrite of something fresher. */
        if (!fromClick) {
          try {
            var cur = typeof cp.currentVisitorConsent === 'function'
              ? cp.currentVisitorConsent() : null;
            if (cur && String(cur.marketing) === String(granted)
                    && String(cur.analytics) === String(granted)) return;
          } catch (e) { /* no record yet — fall through and write ours */ }
        }
        cp.setTrackingConsent(consent, function () {
          log('Shopify customer privacy →', granted ? 'granted' : 'denied');
        });
      } catch (e) { /* API not on this page */ }
    }
    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function (err) { if (!err) send(); }
        );
      } else { send(); }
    } catch (e) { /* not a Shopify storefront */ }
  }

  var choice = saved();
  // Shopify keeps its own record of consent and cannot read ours, so a decision
  // taken on an earlier visit has to be replayed to it on every page load.
  if (choice === 'granted' || choice === 'denied') tellShopify(choice);

  /* ---- Google: loads for everyone, denied by default ------- */
  window.dataLayer = window.dataLayer || [];
  function gtag() { dataLayer.push(arguments); }
  window.gtag = gtag;

  // Must be the very first thing in the dataLayer, before gtag.js arrives.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',   // language / cart preferences
    security_storage: 'granted',
    wait_for_update: 500                // give a stored decision time to land
  });
  gtag('set', 'ads_data_redaction', true);   // strip ad identifiers while denied
  gtag('set', 'url_passthrough', true);      // keep gclid across pages without cookies

  function grantGoogle() {
    gtag('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted'
    });
    log('Google consent → granted (cookies on)');
  }

  // A stored Accept is applied before config, so the very first hit of the
  // page is already a normal, cookied one rather than an anonymous ping.
  if (choice === 'granted') grantGoogle();
  else log('Google consent → denied (cookieless pings only)');

  (function loadGoogle() {
    var s = document.createElement('script');
    s.async = true;
    s.src = 'https://www.googletagmanager.com/gtag/js?id=' + GOOGLE_TAG;
    (document.head || document.documentElement).appendChild(s);

    /* The tag is still configured — the linker below and the quote form's
       generate_lead both need it — but it must not send a page_view of its
       own. Shopify's Google channel pixel fires page_view explicitly on
       every storefront page, so leaving this true counted every view twice. */
    var cfg = {
      send_page_view: false,
      linker: { domains: LINKED_DOMAINS, accept_incoming: true }
    };
    if (GA_DEBUG) cfg.debug_mode = true;

    gtag('js', new Date());
    gtag('config', GOOGLE_TAG, cfg);   // one tag, every destination
    log('Google tag configured', GOOGLE_TAG, GA_DEBUG ? '(DebugView ON)' : '');
  })();

  /* ---- Meta: nothing at all until Accept ------------------- */
  function bootMeta() {
    if (metaBooted) return;
    metaBooted = true;

    !function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v;
      // Meta's stock snippet assumes a <script> already exists to insert before.
      // Fall back to <head> rather than throwing if one somehow does not.
      s = b.getElementsByTagName(e)[0];
      if (s && s.parentNode) s.parentNode.insertBefore(t, s);
      else (b.head || b.documentElement).appendChild(t);
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* init only. The Meta sales channel's pixel already sends PageView on
       every storefront page; a second one here double-counted it. The init
       stays because Lead — which Shopify cannot see — is still fired below. */
    fbq('init', META_ID);
    log('Meta Pixel booted', META_ID, '(PageView left to Shopify)');

    // Anything the visitor did while the banner was still up.
    var held = metaQueue.splice(0, metaQueue.length);
    held.forEach(function (fn) { try { fn(); } catch (e) { } });
  }

  /* ---- Clarity: loads for everyone, cookieless until Accept - */
  function bootClarity() {
    if (clarityBooted) return;
    clarityBooted = true;

    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0];
      // Same guard as the Meta snippet: <head> if this page has no script tag
      // to insert before. analytics.js itself is one, so this is belt-and-braces.
      if (y && y.parentNode) y.parentNode.insertBefore(t, y);
      else (l.head || l.documentElement).appendChild(t);
    })(window, document, 'clarity', 'script', CLARITY_ID);

    /* Tell Clarity where consent stands. `false` is its cookieless mode:
       it still records, but writes no _clck/_clsk and nothing survives the
       visit. grantClarity() upgrades this when Accept is clicked. */
    try { window.clarity('consent', choice === 'granted'); } catch (e) { }
    log('Clarity booted', CLARITY_ID, choice === 'granted' ? '(cookies on)' : '(cookieless)');

    var held = clarityQueue.splice(0, clarityQueue.length);
    held.forEach(function (fn) { try { fn(); } catch (e) { } });
  }

  function grantClarity() {
    try { window.clarity('consent'); } catch (e) { }
    log('Clarity consent → granted (cookies on)');
  }

  if (choice === 'granted') bootMeta();

  /* Unconditional, and last of the three so a failure here cannot stop
     Google or Meta from starting. */
  bootClarity();

  /* ---- event plumbing -------------------------------------- */
  // Run now if Meta is live, otherwise hold it until a decision.
  function whenMeta(fn) {
    if (metaBooted) { try { fn(); } catch (e) { } return; }
    if (metaQueue.length < 20) metaQueue.push(fn);   // bounded: never grow unwatched
  }

  function whenClarity(fn) {
    if (clarityBooted) { try { fn(); } catch (e) { } return; }
    if (clarityQueue.length < 20) clarityQueue.push(fn);
  }

  // An "item" is {code, name, price, qty}. Both vendors want the same
  // facts in different shapes, so normalise once here.
  function norm(it) {
    it = it || {};
    return {
      code: String(it.code || it.id || ''),
      name: String(it.name || it.code || 'MagnuMaster'),
      price: Number(it.price) || 0,
      qty: Number(it.qty) || 1
    };
  }

  function fire(metaEvent, googleEvent, items) {
    items = (items || []).map(norm);
    var value = items.reduce(function (s, i) { return s + i.price * i.qty; }, 0);
    log('event', googleEvent, '/', metaEvent,
        '→ Clarity only; Shopify reports this one to Google and Meta',
        { value: value, items: items });

    /* Google and Meta are NOT sent from here. Shopify's sales-channel pixels
       already report view_item / add_to_cart / begin_checkout, and their Meta
       equivalents, to the same tag and the same pixel. Sending them here as
       well counted each one twice — read the note at the top of this file
       before you are tempted to put them back. `metaEvent` is kept in the
       signature because the call sites and this log line still name it. */

    // Clarity takes an event name and string tags only — no schema, no value
    // field. Tags are what its dashboard filters replays on.
    whenClarity(function () {
      try {
        clarity('event', googleEvent);
        clarity('set', 'mm_value', String(Math.round(value)));
        if (items.length) clarity('set', 'mm_model', items[0].code || items[0].name);
      } catch (e) { log('clarity blocked', e); }
    });
  }

  window.MM_TRACK = {
    viewContent: function (item) { fire('ViewContent', 'view_item', [item]); },
    addToCart: function (item) { fire('AddToCart', 'add_to_cart', [item]); },
    beginCheckout: function (items) { fire('InitiateCheckout', 'begin_checkout', items); },

    // Lead carries no product value — it's an enquiry, not a sale.
    lead: function (detail) {
      detail = detail || {};
      log('event generate_lead / Lead', detail);
      try {
        gtag('event', 'generate_lead', {
          currency: CUR, value: 0,
          form_name: detail.name || 'Quote request',
          model: detail.model || 'unspecified'
        });
      } catch (e) { log('gtag blocked', e); }
      whenMeta(function () {
        try {
          fbq('track', 'Lead', {
            content_name: detail.name || 'Quote request',
            content_category: detail.model || 'unspecified'
          });
        } catch (e) { log('fbq blocked', e); }
      });
      whenClarity(function () {
        try {
          clarity('event', 'generate_lead');
          clarity('set', 'mm_model', detail.model || 'unspecified');
        } catch (e) { log('clarity blocked', e); }
      });
    }
  };

  /* ---- the banner ------------------------------------------ */
  /* The banner is built before main.js has fetched a dictionary (consent has to
     be asked as early as possible), so it carries its own copy of the five
     languages rather than waiting on MM_I18N. */
  var COPY = {
    en: { body:'We use analytics and marketing cookies to improve our shop. They are only loaded with your consent.',
          accept:'Accept all', decline:'Essential only', more:'Privacy policy', aria:'Cookie notice' },
    de: { body:'Wir verwenden Cookies für Analyse und Marketing, um unseren Shop zu verbessern. Diese werden nur mit Ihrer Zustimmung geladen.',
          accept:'Alle akzeptieren', decline:'Nur notwendige', more:'Datenschutz', aria:'Cookie-Hinweis' },
    it: { body:'Utilizziamo cookie di analisi e marketing per migliorare il nostro shop. Vengono caricati solo con il suo consenso.',
          accept:'Accetta tutti', decline:'Solo necessari', more:'Privacy', aria:'Informativa cookie' },
    es: { body:'Utilizamos cookies de análisis y marketing para mejorar nuestra tienda. Solo se cargan con su consentimiento.',
          accept:'Aceptar todo', decline:'Solo esenciales', more:'Privacidad', aria:'Aviso de cookies' },
    fr: { body:"Nous utilisons des cookies d'analyse et de marketing pour améliorer notre boutique. Ils ne sont chargés qu'avec votre consentement.",
          accept:'Tout accepter', decline:'Essentiels uniquement', more:'Confidentialité', aria:'Avis sur les cookies' }
  };
  var LANG = (function () {
    var l; try { l = localStorage.getItem('mm_lang'); } catch (e) {}
    if (!COPY[l]) l = (document.documentElement.lang || '').slice(0, 2).toLowerCase();
    if (!COPY[l]) l = (navigator.language || 'en').slice(0, 2).toLowerCase();
    return COPY[l] ? l : 'en';
  })();
  var T = COPY[LANG];

  var bannerEl = null;

  function render(el, T) {
    el.setAttribute('aria-label', T.aria);
    el.innerHTML =
      '<p class="cookie__txt">' + T.body +
      ' <a href="/pages/privacy">' + T.more + '</a></p>' +
      '<div class="cookie__btns">' +
      '<button type="button" class="cookie__btn" data-consent="denied">' + T.decline + '</button>' +
      '<button type="button" class="cookie__btn cookie__btn--yes" data-consent="granted">' + T.accept + '</button>' +
      '</div>';
  }

  function banner() {
    var el = document.createElement('div');
    bannerEl = el;
    el.className = 'cookie';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-live', 'polite');
    render(el, T);

    el.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-consent]');
      if (!btn) return;
      var state = btn.getAttribute('data-consent');
      remember(state);
      tellShopify(state, true);
      log('banner →', state);
      // Clarity is already running either way — Accept only upgrades it.
      if (state === 'granted') { grantGoogle(); bootMeta(); grantClarity(); }
      else metaQueue.length = 0;      // Google and Clarity stay cookieless
      el.remove();
      // The language notice docks in the same corner and waits for this.
      document.body.dispatchEvent(new CustomEvent('mm:consent-closed'));
    });

    document.body.appendChild(el);
    requestAnimationFrame(function () { el.classList.add('show'); });
  }

  if (choice !== 'granted' && choice !== 'denied') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', banner);
    else banner();
  }

  // Lets the privacy page offer a "change your choice" link.
  window.MM_CONSENT = {
    reset: function () { try { localStorage.removeItem(KEY); } catch (e) { } location.reload(); },
    // The banner has to paint before the country lookup returns, so it can end
    // up a language behind the page. main.js calls this once the two agree.
    relang: function (l) {
      if (bannerEl && bannerEl.isConnected && COPY[l]) render(bannerEl, COPY[l]);
    },
    // One-line health check for the console — "is tracking actually on?"
    status: function () {
      return {
        consent: saved() || 'undecided',
        googleTag: GOOGLE_TAG, googleLoaded: typeof window.gtag === 'function',
        metaId: META_ID, metaLoaded: metaBooted && typeof window.fbq === 'function',
        clarityId: CLARITY_ID, clarityLoaded: clarityBooted && typeof window.clarity === 'function',
        gaDebug: GA_DEBUG, mmDebug: MM_DEBUG,
        queuedMetaEvents: metaQueue.length,
        queuedClarityEvents: clarityQueue.length
      };
    }
  };

  log('status', window.MM_CONSENT.status());
})();
